/**
 * Workplan Generation Context
 * Manages selected items across tabs for AI-powered workplan generation
 * Phase 4: Cross-tab state management for workplan generation
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

// Import entity types
interface PastProjectEntity {
  id: string;
  entity_type: string;
  data: {
    project_id?: string;
    artifact_id?: string;
    project_name: string;
    client_name: string;
    project_type?: string;
    service_line?: string;
    project_status?: 'completed' | 'ongoing' | 'planned' | 'on_hold';
    contract_value?: number;
    start_date?: string;
    completion_date?: string;
    scope_description?: string;
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

interface TechnicalApproachEntity {
  id: string;
  entity_type: string;
  data: {
    approach_id?: string;
    approach_name: string;
    service_line?: string;
    methodology_summary?: string;
    key_steps?: string[];
    innovations?: string[];
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

interface ComplianceEntity {
  id: string;
  entity_type: string;
  data: {
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

interface WorkplanGenerationContextType {
  selectedProject: PastProjectEntity | null;
  selectedApproaches: TechnicalApproachEntity[];
  selectedCompliance: ComplianceEntity[];
  
  setSelectedProject: (project: PastProjectEntity | null) => void;
  
  addApproach: (approach: TechnicalApproachEntity) => void;
  removeApproach: (approachId: string) => void;
  clearApproaches: () => void;
  
  addCompliance: (compliance: ComplianceEntity) => void;
  removeCompliance: (complianceId: string) => void;
  clearCompliance: () => void;
  
  clearAll: () => void;
  
  isReady: boolean;
}

const WorkplanGenerationContext = createContext<WorkplanGenerationContextType | undefined>(undefined);

export function WorkplanGenerationProvider({ children }: { children: ReactNode }) {
  const [selectedProject, setSelectedProject] = useState<PastProjectEntity | null>(null);
  const [selectedApproaches, setSelectedApproaches] = useState<TechnicalApproachEntity[]>([]);
  const [selectedCompliance, setSelectedCompliance] = useState<ComplianceEntity[]>([]);

  const addApproach = (approach: TechnicalApproachEntity) => {
    setSelectedApproaches(prev => {
      // Avoid duplicates
      if (prev.some(a => a.id === approach.id)) {
        return prev;
      }
      return [...prev, approach];
    });
  };

  const removeApproach = (approachId: string) => {
    setSelectedApproaches(prev => prev.filter(a => a.id !== approachId));
  };

  const clearApproaches = () => {
    setSelectedApproaches([]);
  };

  const addCompliance = (compliance: ComplianceEntity) => {
    setSelectedCompliance(prev => {
      // Avoid duplicates
      if (prev.some(c => c.id === compliance.id)) {
        return prev;
      }
      return [...prev, compliance];
    });
  };

  const removeCompliance = (complianceId: string) => {
    setSelectedCompliance(prev => prev.filter(c => c.id !== complianceId));
  };

  const clearCompliance = () => {
    setSelectedCompliance([]);
  };

  const clearAll = () => {
    setSelectedProject(null);
    setSelectedApproaches([]);
    setSelectedCompliance([]);
  };

  // Check if we have minimum requirements for generation
  const isReady = selectedProject !== null;

  const value: WorkplanGenerationContextType = {
    selectedProject,
    selectedApproaches,
    selectedCompliance,
    setSelectedProject,
    addApproach,
    removeApproach,
    clearApproaches,
    addCompliance,
    removeCompliance,
    clearCompliance,
    clearAll,
    isReady,
  };

  return (
    <WorkplanGenerationContext.Provider value={value}>
      {children}
    </WorkplanGenerationContext.Provider>
  );
}

export function useWorkplanGeneration() {
  const context = useContext(WorkplanGenerationContext);
  if (context === undefined) {
    throw new Error('useWorkplanGeneration must be used within a WorkplanGenerationProvider');
  }
  return context;
}

